import $ from 'jquery';
import _ from 'underscore';
import { jqGetElemById } from 'util/global';
import Raven from 'util/raven';


const MAILGUN_API_KEY = 'pubkey-7bfc8ckzpnzm6x-a4d86olnmp5lbj5p2';


/**
 * @callback MailgunValidatorSuccess
 * @param {object} data
 *      @param {boolean} data.is_valid
 *      @param {object} data.parts
 *          @param {string} data.parts.local_part - 'john.smith@example.com'
 *          @param {string} data.parts.domain - 'example.com'
 *          @param {string} data.parts.display_name
 *      @param {string} data.address - 'john.smith@example.com'
 *      @param {string} data.did_you_mean
 * @see https://api.mailgun.net/v2/address
 */

/**
 * @callback MailgunValidatorError
 * @param {string} errorMsg
 */

/**
 * Mailgun Address Validation Plugin
 * Adapted from https://github.com/calebrash/validator-demo/blob/gh-pages/mailgun_validator.js
 * @param {JQuery} $fields
 * @param {function} [inProgress] - called when request is made to validator
 * @param {MailgunValidatorSuccess} onSuccess - called when validator has returned a result
 * @param {MailgunValidatorError} onError - ajax, jsonp, or other request error
 */
function mailgunValidator({
    $fields,
    inProgress = _.noop,
    onSuccess = _.noop,
    onError = _.noop,
} = {}) {
    $fields.on('blur', (e) => {
        const input = e.target;
        const addressText = $(input).val();

        // don't run validator without input
        if (!addressText) {
            return;
        }

        let success = false;

        // length check
        if (addressText.length > 512) {
            onError.call(input, `address is too long (${addressText.length})`);
            return;
        }

        // validator is in progress
        inProgress.call(input);

        // make ajax call to get validation results
        $.ajax({
            type: 'GET',
            url: 'https://api.mailgun.net/v2/address/validate?callback=?',
            data: {
                address: addressText,
                api_key: MAILGUN_API_KEY,
            },
            dataType: 'jsonp',
            crossDomain: true,
        }).done((data) => {
            success = true;
            onSuccess.call(input, data);
        }).fail((request, statusText) => {
            success = true;
            onError.call(input, `http error: ${statusText} (${request.status})`);
        });

        // timeout in case of some kind of internal server error
        setTimeout(() => {
            if (!success) {
                onError.call(input, 'timeout');
            }
        }, 30000);
    });
}


/**
 * @controller
 * @param {JQuery} $container
 */
export function controller($container) {
    const $emailTooltip = $container.find('#email-validation');
    const $emailField = $container.find('#id_email');

    /**
    * Process successful email validation result -- note, this is a successful request, not a
    * valid email address
    * @param {object} result - json result from Mailgun
    */
    const mailgunSuccess = (result) => {
        // remove current status classes
        $emailTooltip.removeClass('active didyoumean invalid');
        $emailField.removeClass('email-invalid');

        // if not a valid email address
        if (!result.is_valid || result.did_you_mean) {
            // mark the input invalid
            $emailField.addClass('email-invalid');

            // if has typo suggestion
            if (result.did_you_mean) {
                // add did-you-mean text
                jqGetElemById('didyoumean-txt').text(result.did_you_mean);
                // save did-you-mean value
                $emailTooltip
                    .data('didyoumean', result.did_you_mean)
                    .addClass('active didyoumean');

            // else invalid email format, wrong domain, etc
            } else {
                $emailTooltip.addClass('active invalid');
            }
        }
    };

    /**
    * Handle Mailgun errors
    * @param {string} msg - error reason text
    */
    const mailgunError = (msg) => {
        Raven.captureMessage(`Mailgun error: ${msg}`);
    };

    // attach validator
    if ($emailTooltip.length > 0 && $emailField.length > 0) {
        mailgunValidator({
            $fields: $emailField,
            onSuccess: mailgunSuccess,
            onError: mailgunError,
        });

        $emailTooltip.find('.didyoumean-close').on('click', (e) => {
            e.preventDefault();
            if ($(e.currentTarget).hasClass('accept')) {
                $emailField.val($emailTooltip.data('didyoumean'));
            }
            $emailTooltip.removeClass('active');
            $emailField.removeClass('email-invalid');
        });
    }
}
